import {Component, ElementRef, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormGroup, Validators} from '@angular/forms';
import {PagesHttpService} from '../../../services/http-services/pages/pages-http.service';
import {GetPageApiData, PutPageApiData} from '../../../services/http-services/pages/pages-http-interfaces';
import {ToastrService} from 'ngx-toastr';
import {ActivatedRoute} from '@angular/router';
import {BlogTypeEnum} from '../../../enums/blog-enums';
import {AboutUsTabEnums} from '../../../enums/pages-enums';
import {BankAccountRegex, MbRegex, MobileRegex, PhoneRegex, PibRegex} from '../../../constants/general-variables';
import {timeRangeValidator} from '../../../validators/form-validators.validator';

@Component({
  selector: 'gr-pages',
  templateUrl: './pages.component.html',
})
export class PagesComponent implements OnInit {
  pageId = '';
  updatePageForm!: FormGroup;
  formTriedToSubmit = false;
  isFormDisabled = true;
  isPageCreated = false;
  aboutUsActiveTab = AboutUsTabEnums.mobile;
  isAboutUsGalleryFormDisabled = true;
  aboutUsGalleryForm!: FormGroup;
  galleryFormTriedToSubmit = false;
  imageUrls: any[] = [];
  deletedImagesIds: number[] = [];
  isLoaderActive = false;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  protected readonly AboutUsTabEnums = AboutUsTabEnums;

  constructor(private formBuilder: FormBuilder,
              private activatedRoute: ActivatedRoute,
              private pagesHttpService: PagesHttpService,
              private toastService: ToastrService) {
  }

  ngOnInit(): void {
    this.buildForm();
    this.activatedRoute.params.subscribe((res: any) => {
      this.pageId = res.id;
      this.getSinglePage();
      if (this.pageId === '3') {
        this.buildAboutUsGalleryForm();
      }
    });
  }

  getSinglePage(): void {
    this.pagesHttpService.getSinglePage({pageId: this.pageId}).then((res: GetPageApiData) => {
      this.isPageCreated = res.data.created_at !== res.data.updated_at;
      this.disabledForm();
      this.updatePageForm.patchValue({...res.data});
    });
  }

  buildForm(): void {
    this.updatePageForm = this.formBuilder.group({
      name: ['', Validators.required],
      content: ['', Validators.required]
    });
  }

  enableForm(): void {
    this.isFormDisabled = false;
    this.updatePageForm.enable();
  }
  disabledForm(): void {
    this.isFormDisabled = true;
    this.updatePageForm.disable();
  }

  submitUpdatePageForm(): void {
    this.formTriedToSubmit = true;
    if (this.updatePageForm.valid) {
      this.pagesHttpService.putPage({pageId: this.pageId}, this.updatePageForm.value).then((res: PutPageApiData) => {
        this.disabledForm();
        this.toastService.success(this.isPageCreated ? 'Uspešno ste izmenili stanicu.' : 'Uspešno ste kreirali stanicu.');
        this.isPageCreated = true;
        this.formTriedToSubmit = false;
      });
    }
  }

  // About us gallery
  buildAboutUsGalleryForm(): void {
    this.aboutUsGalleryForm = this.formBuilder.group({
      images: [null, Validators.required],
      deleted_images_ids: [[]]
    });
    this.pagesHttpService.getAboutUsAdmin().then(res => {
      this.imageUrls = res.data.images.map((item: any) => {
        return {fullPath: item.full_path, id: item.id, file: undefined};
      });
      this.aboutUsGalleryForm.controls.images.patchValue(this.imageUrls);
    });
  }

  enableAboutUsGalleryForm(): void {
    this.isAboutUsGalleryFormDisabled = false;
    this.aboutUsGalleryForm.enable();
  }

  disabledAboutUsGalleryForm(): void {
    this.isAboutUsGalleryFormDisabled = true;
    this.aboutUsGalleryForm.disable();
  }

  onFileChange(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files) {
      const selectedFiles = Array.from(input.files);
      selectedFiles.forEach(file => {
        if (file.type !== 'image/jpeg' && file.type !== 'image/png' && file.type !== 'image/jpg' && file.type !== 'image/svg') {
          this.toastService.warning('Pogrešan tip dokumenta. Samo JPG, JPEG, SVG i PNG formati su dozvoljeni.');
          return;
        } else if (file?.size > 10485760) {
          this.toastService.warning(`Slika ${file.name} je veća od 10MB.`);
          return;
        } else {
          this.imageUrls.push({fullPath: URL.createObjectURL(file), id: undefined, file});
        }
      });
      this.aboutUsGalleryForm.controls.images.patchValue(this.imageUrls);
      if (this.fileInput) {
        this.fileInput.nativeElement.value = '';
      }
    }
  }

  removeAllImages(): void {
    this.imageUrls.forEach((image: any) => {
      if (image.id) {
        this.deletedImagesIds.push(image.id);
      }
    });
    this.imageUrls = [];
    this.aboutUsGalleryForm.controls.deleted_images_ids.patchValue(this.deletedImagesIds);
    this.aboutUsGalleryForm.controls.images.patchValue(null);
  }

  removeSingleImage(imageURL: any): void {
    if (!this.isAboutUsGalleryFormDisabled) {
      const imageIndex = this.imageUrls.findIndex((imgUrl: any) => {
        return imgUrl.fullPath === imageURL.fullPath;
      });
      if (imageIndex !== -1) {
        if (imageURL.id) {
          this.deletedImagesIds.push(imageURL.id);
        }
        this.imageUrls.splice(imageIndex, 1);
        this.aboutUsGalleryForm.controls.deleted_images_ids.patchValue(this.deletedImagesIds);
        this.aboutUsGalleryForm.controls.images.patchValue(this.imageUrls.length ? this.imageUrls : null);
      }
    }
  }

  submitEditBusinessProfileForm(): void {
    this.formTriedToSubmit = true;
    if (this.aboutUsGalleryForm.valid) {
      this.isLoaderActive = true;
      const formData: FormData = new FormData();
      for (const key in this.aboutUsGalleryForm.value) {
        if (Array.isArray(this.aboutUsGalleryForm.value[key])) {
          if (key === 'images') {
            this.aboutUsGalleryForm.value[key].forEach((element: any, index: number) => {
              formData.append(`images[${index}][position]`, (index + 1).toString());
              (element.id)
                ? formData.append(`images[${index}][id]`, element.id.toString())
                : formData.append(`images[${index}][file]`, element.file);
            });
          } else {
            this.aboutUsGalleryForm.value[key].forEach((element: any) => {
              formData.append(`${key}[]`, element);
            });
          }
        }
      }
      this.pagesHttpService.postAboutUsGallery(formData).then(res => {
        this.disabledAboutUsGalleryForm();
        this.aboutUsGalleryForm.controls.deleted_images_ids.patchValue([]);
        this.deletedImagesIds = [];
        this.imageUrls = res.data.images.map((item: any) => {
          return {fullPath: item.full_path, id: item.id, file: undefined};
        });
        this.aboutUsGalleryForm.controls.images.patchValue(this.imageUrls);
        this.isLoaderActive = false;
        this.toastService.success(res.message);
      }).catch(({error}: { error: { errors: { [key: string]: string } } }) => {
        this.isLoaderActive = false;
        Object.values(error.errors).forEach(error => this.toastService.error(error));
      });
    }
  }
}
